import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import NoDataComponent from '../../no_data_component.jsx';
import LoadingIndicator from '../../../common/loading_indicator.jsx';
import { cloneDeep } from 'lodash';
import * as d3 from 'd3';
import { datasetsConfigured } from '../../hooks/useDataset.js';

// Used to extends dataset but it's very different in chart init because using a separate Timeline instance
function TimelineWidget({ widgetId, widgetRef, editing }) {
    const settings = useSelector(state => state.dashboard.widgets[widgetId].settings) || {};
    const layout = useSelector(state => state.dashboard.layout[widgetId]);
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [noData, setNoData] = useState(false);
    const contentRef = useRef();

    const initChart = () => {
        if (widgetRef.current && !noData){
            let currentDatasets = settings.datasets;
            if (chartData.length > 0){
                currentDatasets = chartData
            }
            Timeline.init(contentRef.current, {...settings, datasets: currentDatasets});
        } 
    }

    const formatData = () => {
        const datasets = cloneDeep(settings.datasets) || [];
        let notConfigured = !(datasetsConfigured(datasets) || datasets.some((d) => d.value != ""))

        // var _datasets = JSON.parse(JSON.stringify(datasets))
        // _datasets = _datasets.sort((a,b) => a.value - b.value);
        var current_date = Date.now()
        var start_date_d = new Date(datasets[0].date)
        var end_date_d = new Date(datasets.slice(-1)[0].date)
        var start_date = start_date_d.getTime();
        var end_date = end_date_d.getTime();
        var range = end_date - start_date;
        var proportion =((current_date - start_date)/range);

        datasets.forEach((d, i) => {
            // var endAngle = -0.5*Math.PI + (d.value/datasets[0].value)*Math.PI;
            // var progress = (d.value/datasets[0].value).toFixed(2);
            // var value = (((this.state.data || {})[d.dataset_id] || [])[0] || {}).data || d.value
            // d.endAngle = endAngle;
            // d.progress = progress;
            // d.value = value;
            var date = new Date(d.date);
            var milliseconds = date.getTime(); 
            d.milliseconds = milliseconds;
            d.proportion = (milliseconds - start_date)/range;
            d.past = milliseconds < current_date;
            (i == 0) ? d.first = true : null;
            (i == datasets.length-1) ? d.last = true : null;
        });
        
        return {
            notConfigured: notConfigured,
            datasets: datasets,
            proportion: proportion
        };
    };

    const dataLoaded = () => {
        const data = formatData();
        if (data.notConfigured) {
            setNoData(true);
            setLoading(false);
        } else {
            setChartData(data.datasets);
            setLoading(false);
            setNoData(false);
        }
    }

    useEffect(() => {
        dataLoaded();
    }, [settings])
    
    useEffect(() => {
        initChart();
    }, [chartData, layout, editing])

    return (
        loading ? <LoadingIndicator /> : (
          noData ? <NoDataComponent /> :  
            <div className="section-timeline" style={{ width: '100%', height: '100%'}} ref={contentRef}>
                <div className="timeline-chart h-100"></div>
            </div>
        )
    )
}
// TODO: DOM manipulation and widget-header actually appear after the component appear so having wrong initial height
const Timeline = {
    init: function(widgetContainer, settings){
        const {datasets = [], timeline_background_color, elapsed_time_background_color, 
            value_color, label_color, past_circle_color, future_circle_color, 
            past_circle_border_color, future_circle_border_color, 
            past_icon_color, future_icon_color, show_arrow, subtitle_color} = settings;
        d3.select(widgetContainer).select('svg').remove();
        
        if (datasets.length < 2) return;
        var width = widgetContainer.getBoundingClientRect().width - 10;
        var startOffset = datasets[0].value.toString().length < 4 ? 23 : datasets[0].value.toString().length*6 ;
        var endOffset = datasets.slice(-1)[0].value.toString().length > 4 && show_arrow == 'false' ? datasets.slice(-1)[0].value.toString().length*6 : 23;
        var lineWidth = show_arrow=='true' ? (width*0.9-startOffset-endOffset) : (width-startOffset-endOffset);
        var height = widgetContainer.getBoundingClientRect().height + 25;
        var proportion = Math.min(1, (Date.now()-datasets[0].milliseconds)/(datasets.slice(-1)[0].milliseconds - datasets[0].milliseconds));
        var markerBoxWidth = 4;
        var markerBoxHeight = 4;
        var refX = markerBoxWidth / 2;
        var refY = markerBoxHeight / 2;
        var arrowPoints = [[0, 0], [0, 4], [4, 2]];

        var svg = d3.select(widgetContainer).select('.timeline-chart')
        .append('svg')
        .attr('width', width)
        .attr('height', '100%');

        svg.append('line')
        .style("stroke", timeline_background_color)
        .style("stroke-width", 10)
        .attr("x1", startOffset)
        .attr("y1", height/2 )
        .attr("x2", startOffset + lineWidth)
        .attr("y2", height/2);

        if (show_arrow == 'true'){
          var arrowColor = datasets.slice(-1)[0].past ? elapsed_time_background_color :timeline_background_color;
          svg
          .append('defs')
          .append('marker')
          .attr('id', 'arrow')
          .attr('viewBox', [0, 0, markerBoxWidth, markerBoxHeight])
          .attr('refX', refX)
          .attr('refY', refY)
          .attr('markerWidth', markerBoxWidth)
          .attr('markerHeight', markerBoxHeight)
          .attr('orient', 'auto-start-reverse')
          .append('path')
          .attr('d', d3.line()(arrowPoints))
          .attr('fill', arrowColor);
        
          svg.append('line')
          .style("stroke", arrowColor)
          .style("stroke-width", 10)
          .attr("x1", startOffset + lineWidth)
          .attr("y1", height/2 )
          .attr("x2", startOffset + lineWidth + 0.1*width)
          .attr("y2", height/2)
          .attr('marker-end', 'url(#arrow)'); 
        }


        svg.append('line')
        .style("stroke", elapsed_time_background_color)
        .style("stroke-width", 10)
        .attr("x1", startOffset)
        .attr("y1", height/2 )
        .attr("x2", startOffset + lineWidth*proportion)
        .attr("y2", height/2); 

        var chart = svg.selectAll('circle').append("g")
        .data(datasets)
        .enter()
        .append('circle')
        .attr('r', 20) 
        .attr('cy', height/2)
        .attr('cx', function(d) { return startOffset + lineWidth*d.proportion} ) 
        .attr('fill', function(d){ return (d.past ? past_circle_color :future_circle_color)})
        .style("stroke", function(d){ return (d.past ? past_circle_border_color :future_circle_border_color)})
        .style("stroke-width", 5)
        .attr("opacity", 1);

        svg.selectAll('.value')
        .data(datasets)
        .enter()
        .append('text')
        .attr('x', function(d) { return startOffset + lineWidth*d.proportion})
        .attr('y', height/2 - 35)
        .attr("text-anchor", "middle")
        .style('fill', value_color)
        .style('font-size', 20)
        .style('font-weight', 600)
        .text(function(d) {return d.value})

        svg.selectAll('.label')
        .data(datasets)
        .enter()
        .append('text')
        .attr('x', function(d) { return startOffset + lineWidth*d.proportion})
        .attr('y', height/2 + 40)
        .attr("text-anchor", "middle")
        .style('fill', label_color)
        .text(function(d) {return d.label})

        svg.selectAll('.subtitle')
        .data(datasets)
        .enter()
        .append('text')
        .attr('x', function(d) { return startOffset + lineWidth*d.proportion})
        .attr('y', height/2 + 58)
        .attr("text-anchor", "middle")
        .style('fill', subtitle_color)
        .style('font-size', 12)
        .text(function(d) {return d.subtitle})

        svg.selectAll('.icon')
        .data(datasets)
        .enter()
        .append("foreignObject")
        .attr("x", function(d) { return startOffset + lineWidth*d.proportion -10 })
        .attr("y", height/2-10)
        .attr("width", 20)
        .attr("height", 20)
        .style('fill', function(d){ return (d.past ? past_icon_color : future_icon_color)})
        .style('color', function(d){ return (d.past ? past_icon_color : future_icon_color)})
        .html(function(d) {
          return '<p style="font-size: 18px; text-align: center" class="adalicon icon-' + d.image + '">' + '</p>'
        });
    }
}

export default TimelineWidget;
